// libs
import React, { useState } from 'react';
// components
import { useForm, Controller } from "react-hook-form";
import { ToastContainer, toast } from 'react-toastify';
import Navigation from "../../components/navbar";
import BreadcrumbNav from "../../components/breadcrumb-nav";
import GalleryBox from '../../components/gallery-box';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
// css
import "../../assets/css/style.css";
// config & functions 
import ajaxCall from '../../utils/ajaxcall';
import config from '../../config';
import { useSelectedAccount } from '../../context/account-context';

function getUploadErrorMessage(apiCall) {
    const message = apiCall?.context?.message;
    if (!message) {
        return 'Something went wrong. Please try again :(';
    }
    if (typeof message === 'string') {
        return message;
    }
    // ErrorHandler stores response.data here; axios success stores full response
    const body = message?.data && typeof message.data === 'object' && !Array.isArray(message.data)
        ? message.data
        : message;
    return (
        body?.message ||
        body?.msg ||
        body?.error ||
        body?.context?.message ||
        (typeof body === 'string' ? body : null) ||
        'Something went wrong. Please try again :('
    );
}

function isUploadSuccessful(apiCall) {
    if (!apiCall?.status) {
        return false;
    }
    const body = apiCall?.context?.message?.data;
    if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'status')) {
        return Boolean(body.status);
    }
    return true;
}

function Image({token, orgInfo}) {
    const { selectedAccountDomain, hasSelectedAccount } = useSelectedAccount();
    const [DisableSubmitButton, setDisableSubmitButton] = useState(false);
    const [BrowseImage, setBrowseImage] = useState(false);
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        reset
    } = useForm();

    const onSubmit = async (data) => {
        if (!hasSelectedAccount || !selectedAccountDomain) {
            toast.error('Please choose a seller account in the top navigation');
            return;
        }

        setDisableSubmitButton(true);
        try {
            const formData = new FormData();
            formData.append("file", data.fileName[0]);
            formData.append("imgLocation", data.imgLocation);
            formData.append("projectName", "inhouseEcommSAS");
            formData.append("fileNamePrefix", data.prefix);
            formData.append("clientDomain", `sas-ecommerce/${selectedAccountDomain}`);

            let apiCall = await ajaxCall.postCall(`${config.api.image}/upload`, formData, token);
            if (isUploadSuccessful(apiCall)) {
                toast.info('File updated successfully');
                reset();
            } else {
                toast.error(getUploadErrorMessage(apiCall));
            }
        } catch (e) {
            toast.error('Something went wrong. Please try again :(');
        } finally {
            setDisableSubmitButton(false);
        }
    }

    const updateChangeEvent = (value, field) => {
		field.onChange(value);
	}

	return (
        <>
            <Navigation orgInfo={orgInfo} />
            <Container>
                <Row>
                    <Col>
                        <div className="mt-3">
                            <h3 className="fs-20rem text-left vt-txt-dark-blue">Upload Images</h3>
                            <BreadcrumbNav list={[{label: 'Home', url: '/'}, {label: 'Images', url: false}]} />
                        </div>
                    </Col>
                    <Col>
                        <div className="text-right d-flex justify-content-end h-100 align-items-center">
                            <Button variant="primary" type="button" size="md" disabled={!hasSelectedAccount} onClick={() => setBrowseImage(true)} className="vt-primary-btn border-0 text-white fs-14rem">VIEW IMAGES</Button>
                        </div>
                    </Col>
                    <hr />
                </Row>
                <form action="" method="" onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                        <Col md="4" lg="4" className="mb-3">
                            <div><label className="form-label fs-13rem fw-bold">Image Location</label></div>
                            <Controller
                                name='imgLocation'
                                defaultValue={""}
                                rules={{required: true}}
                                control={control}
                                render={({field}) => (
                                    <select value={field.value} className="form-control custom-select" name="imgLocation" {...register("imgLocation", { required: true })} onChange={(e) => {updateChangeEvent(e, field)}}>
                                        <option value=""> Choose...</option>
                                        <option value="generic">Generic (Freestyle)</option>
                                        <option value="banner">Banner (1800x750)</option>
                                        <option value="product">Product (1200x1200)</option>
                                        <option value="card">Card (416x416)</option>
                                    </select>
                                )}
                            />
                            <small className="form-text text-danger">
                                {errors.imgLocation?.type === "required" && "Please update image type"}
                            </small>
                        </Col>
                        <Col md="4" lg="4" className="mb-3">
                            <div><label className="form-label fs-13rem fw-bold">Prefix</label></div>
                            <input type="text" className="form-control" name="prefix" {...register("prefix", { required: false, minLength: 1, maxLength: 5 })} />
                            <small className="form-text text-danger">
                                {errors.prefix?.type === "required" && "Prefix is mandatory"}
                            </small>
                            <small className="form-text text-danger">
                                {errors.prefix?.type === "minLength" && "Enter atleast 1 characters"}
                            </small>
                            <small className="form-text text-danger">
                                {errors.prefix?.type === "maxLength" && "Only 5 characters allowed"}
                            </small>
                        </Col>
                        <Col md="4" lg="4" className="mb-3">
                            <div><label htmlFor="formFile" className="form-label fs-13rem fw-bold">Choose file (jpeg, png)</label></div>
                            <input defaultValue={``} className="form-control" type="file" id="formFile" name="fileName" {...register("fileName", { required: true })} />
                            <small className="form-text text-danger">
                                {errors.fileName?.type === "required" && "File is mandatory"}
                            </small>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-end mb-4">
                            <Button variant="primary" type="submit" disabled={DisableSubmitButton || !hasSelectedAccount} className="vt-primary-btn border-0 text-white fs-14rem">{(DisableSubmitButton)?`LOADING...`:`UPLOAD`}</Button>
                        </Col>
                    </Row>
                </form>
            </Container>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            {(BrowseImage)? <GalleryBox orgInfo={orgInfo} setbrowseimage={setBrowseImage} token={token} />:null}
        </>
	)
}

export default Image;
